import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Spinner } from 'react-bootstrap';
import { useVMSControlContext } from './VMSControlContext'; // Adjust the import path as needed
import { getUtcToLocalByBreakpoints } from '@awp/helpers/utilsReact';
import { useDispatch } from 'react-redux';
import { cancelVMSTimer, setVMSState, setVMSTimer } from '@awp/stores/variableMessageSignSlice';
import { STATE_STATUS } from '@awp/constants';
import { toast } from 'react-toastify';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import VMSDateModal from './VMSDateModal';

/**
 * ErrorMsg Component
 * Displays an error message when the sign fails to set its mode.
 *
 * @param {Object} props - Component properties.
 * @param {string} props.location - Location of the sign.
 * @param {boolean} props.isAuto - Flag indicating whether the sign should be in auto mode.
 * @param {string} props.statusError - Error message from the status.
 * @returns {JSX.Element} - The rendered error message.
 */
const ErrorMsg = ({ location, isAuto, statusError }) => {
  return (
    <div className="msg-container">
      <span className="msg-description">
        The sign <span style={{ fontWeight: 'bold' }}>{location}</span> was not set to {isAuto ? 'Auto' : 'Manual'},
        there was an error.
      </span>
    </div>
  );
};

/**
 * SuccessMsg Component
 * Displays a success message when the sign is successfully set to a mode.
 *
 * @param {Object} props - Component properties.
 * @param {string} props.location - Location of the sign.
 * @param {boolean} props.isAuto - Flag indicating whether the sign should be in auto mode.
 * @returns {JSX.Element} - The rendered success message.
 */
const SuccessMsg = ({ location, isAuto }) => {
  return (
    <div className="msg-container">
      <span className="msg-description">
        The sign <span style={{ fontWeight: 'bold' }}>{location}</span> has been successfully set to{' '}
        {isAuto == 'N' ? 'Auto' : 'Manual'}
      </span>
    </div>
  );
};

/**
 * Gets the next 30-minute mark from the current time.
 * @returns {string} - The next 30-minute mark in ISO format.
 */
const getNext30MinuteMark = () => {
  const now = new Date();
  const minutes = now.getMinutes();
  const next30Minutes = Math.ceil(minutes / 30) * 30;
  if (next30Minutes === 60) {
    now.setHours(now.getHours() + 1);
    now.setMinutes(0);
  } else {
    now.setMinutes(next30Minutes);
  }
  now.setSeconds(0);
  now.setMilliseconds(0);

  return now.toISOString().slice(0, 16).replace('T', ' ');
};

/**
 * VMSControlSignDetails Component
 * Displays details and controls for a Variable Message Sign (VMS).
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.sign - The sign object containing details for the VMS.
 * @returns {JSX.Element} - The rendered VMSControlSignDetails component.
 */
const VMSControlSignDetails = ({ sign }) => {
  const dispatch = useDispatch();
  const [entityErrors, setEntityErrors] = useState(null);

  const {
    currentBreakpoint,
    VMSData,
    setLoading,
    loading,
    status,
    statusError,
    setVariableMessageSign,
    setShowSetDateTimeModal,
    showSetDateTimeModal,
    variableMessageSign,
  } = useVMSControlContext();

  // Set the default value based on sign.IsAuto
  const isAuto = sign.IsAuto === 'N' ? false : true;
  const hasTimer = sign.ResetTime !== null ? true : false;

  const userNameWithNbsp = sign.UserName ? sign.UserName.replace(/ /g, '\u00a0') : '';
  const [vmsSign, setVMSSign] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [timerDisabled, setTimerDisabled] = useState(false);

  /**
   * Retrieves the VMS sign details based on weatherStationId and vmsSignId.
   *
   * @param {number} weatherStationId - The ID of the weather station.
   * @param {number} vmsSignId - The ID of the VMS sign.
   * @returns {Object|null} - The VMS sign object or null if not found.
   */
  const getVMSSign = (weatherStationId, vmsSignId) => {
    return VMSData.find((station) => {
      return (
        station.vmsSigns &&
        station.vmsSigns.length > 0 &&
        station.vmsSigns.some(
          (vmsSign) => vmsSign.WeatherStationId === weatherStationId && vmsSign.VariableMessageSignId === vmsSignId,
        )
      );
    })?.vmsSigns?.find((vms) => {
      return vms.VariableMessageSignId === vmsSignId;
    });
  };

  /**
   * Toggles the state of the VMS sign between Auto and Manual.
   *
   * @param {Event} e - The change event.
   */
  const toggleVMSState = async (e) => {
    const weatherStationId = parseInt(e.target.dataset.weatherstationid || 0);
    const vmsSignId = parseInt(e.target.dataset.signid || 0);
    const newValue = e.target.value;

    const vmsSign = getVMSSign(weatherStationId, vmsSignId);
    if (vmsSign) {
      setVMSSign(vmsSign);

      const payload = {
        vmsSignId: vmsSignId,
        weatherStationId: weatherStationId,
        weatherStationNumber: vmsSign.WeatherStationNumber,
        stateKey: vmsSign.StateKey,
        stateValue: newValue,
      };
      setLoading(true);
      await dispatch(setVMSState(payload));
    }
  };

  // Change index every 5 seconds, simulates message page changes
  useEffect(() => {
    if (!sign) return;
    const interval = setInterval(() => {
      setPageIndex((pageIndex) => (pageIndex + 1) % sign.signValue.length);
    }, 5000); // 5000 ms = 5 seconds

    return () => clearInterval(interval);
  }, [VMSData, sign]); // Ensure totalPageCount is included in the dependency array

  useEffect(() => {
    if (vmsSign) {
      if (status.stateSet === STATE_STATUS.FAILED) {
        toast.error(<ErrorMsg location={vmsSign.Location} isAuto={vmsSign.IsAuto} statusError={statusError} />, {
          autoClose: 5000,
        });
        setLoading(false); // Set loading state to false when data fetching completes
        setVMSSign(null);
      } else if (status.stateSet === STATE_STATUS.SUCCESS) {
        toast.success(<SuccessMsg location={vmsSign.Location} isAuto={vmsSign.IsAuto} />, { autoClose: 5000 });
        setLoading(false); // Set loading state to false when data fetching completes
        setVMSSign(null);
      } else if (status.stateTimer === STATE_STATUS.FAILED) {
        toast.success('VMS Timer was not set, there was an error.', { autoClose: 5000 });
        setLoading(false); // Set loading state to false when data fetching completes
        setVMSSign(null);
      } else if (status.stateTimer === STATE_STATUS.SUCCESS) {
        toast.success('VMS Timer has been set.', { autoClose: 5000 });
        setLoading(false); // Set loading state to false when data fetching completes
        setVMSSign(null);
      }
    }
  }, [status]);

  /**
   * Sets the state timer for the VMS sign to the specified reset time.
   *
   * @param {string} resetTime - The reset time to be set.
   */
  const setStateTimer = async (resetTime) => {
    setTimerDisabled(true);
    const { weatherStationId, vmsSignId } = variableMessageSign;
    const vmsSign = getVMSSign(weatherStationId, vmsSignId);

    if (vmsSign) {
      setVMSSign(vmsSign);

      const payload = {
        vmsSignId: vmsSignId,
        resetTime: resetTime,
      };

      await dispatch(setVMSTimer(payload));
    }
    setTimerDisabled(false);
  };

  /**
   * Cancels the state timer for the VMS sign.
   *
   * @param {Event} e - The click event.
   */
  const cancelStateTimer = async (e) => {
    setTimerDisabled(true);
    const vmsSignId = parseInt(e.target.dataset.signid || 0);
    const payload = {
      vmsSignId: vmsSignId,
    };

    await dispatch(cancelVMSTimer(payload));
    setTimerDisabled(false);
  };

  return (
    <>
      <Card className="mb-3 vms-sign-card">
        <Card.Body>
          <div className="vms-location">{sign.SignName}</div>
          <div
            className="sign-container"
            style={{ fontFamily: 'VMS-Regular' }}
            dangerouslySetInnerHTML={{ __html: sign.signValue[pageIndex] }}
          />
          <Form>
            <div className="stateContainer">
              <div className="loadingOverlay" style={{ display: loading ? 'block' : 'none' }}></div>
              <div className="overlaySpinner" style={{ display: loading ? 'flex' : 'none' }}>
                <Spinner animation="border" variant="primary" />
              </div>
              <Form.Group className="radio-group">
                <Form.Check
                  type="radio"
                  label="Auto"
                  name={`auto-manual-${sign.VariableMessageSignId}`}
                  id={`auto-${sign.VariableMessageSignId}`}
                  checked={isAuto}
                  onChange={toggleVMSState}
                  className="radio-inline"
                  data-weatherstationid={sign.WeatherStationId}
                  data-signid={sign.VariableMessageSignId}
                  value={'true'}
                />
                <Form.Check
                  type="radio"
                  label="Manual"
                  name={`auto-manual-${sign.VariableMessageSignId}`}
                  id={`manual-${sign.VariableMessageSignId}`}
                  checked={!isAuto}
                  onChange={toggleVMSState}
                  className="radio-inline"
                  data-weatherstationid={sign.WeatherStationId}
                  data-signid={sign.VariableMessageSignId}
                  value={'false'}
                />
              </Form.Group>
            </div>
          </Form>
          <div className="auto-set-info">
            {sign.EffectiveTimestamp != null ? (
              <>
                {isAuto ? 'Auto' : 'Manual'} set{' '}
                <span className="bold-timestamp">
                  {getUtcToLocalByBreakpoints(
                    sign.EffectiveTimestamp,
                    currentBreakpoint,
                    'yyyy-MM-dd HH:mm',
                    'yyyy-MM-dd HH:mm',
                  )}
                </span>{' '}
                by {userNameWithNbsp}.{' '}
                {hasTimer ? (
                  <>
                    Returns to Auto on{' '}
                    <span className="bold-timestamp">
                      {getUtcToLocalByBreakpoints(
                        sign.ResetTime,
                        currentBreakpoint,
                        'yyyy-MM-dd HH:mm',
                        'yyyy-MM-dd HH:mm',
                      )}
                    </span>{' '}
                  </>
                ) : null}
              </>
            ) : (
              <div>&nbsp;</div>
            )}
          </div>
          <div>&nbsp;</div>
          {!isAuto ? (
            <div className="mb-1 pl-3">
              <Button
                className="mr-2 my-1"
                variant={hasTimer ? `outline-danger` : `outline-primary`}
                disabled={timerDisabled}
                data-weatherstationid={sign.WeatherStationId}
                data-signid={sign.VariableMessageSignId}
                onClick={(e) => {
                  if (hasTimer) {
                    cancelStateTimer(e);
                  } else {
                    setVariableMessageSign({
                      weatherStationId: parseInt(e.target.dataset.weatherstationid || 0),
                      vmsSignId: parseInt(e.target.dataset.signid || 0),
                    });
                    setShowSetDateTimeModal(true);
                  }
                }}
              >
                {hasTimer ? 'Cancel Timer' : 'Set Timer'}
              </Button>
            </div>
          ) : null}
        </Card.Body>
      </Card>
      <VMSDateModal
        showModal={showSetDateTimeModal}
        setShowModal={setShowSetDateTimeModal}
        saveDateTime={setStateTimer}
      />
      <AwpValidationErrorModal setErrors={setEntityErrors} errors={entityErrors} />
    </>
  );
};

export default VMSControlSignDetails;
